<template>
  <HrPageLayout
    title="Audit Logs"
    subtitle="View system activity and audit trail"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/dashboard' },
      { label: 'System Management' },
      { label: 'Audit Logs' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="loadLogs" :disabled="loading">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
    </template>

    <div class="audit-logs-content">
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Filters</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-d-flex hr-flex-wrap hr-gap-2">
            <div class="hr-filter-group">
              <label class="hr-form-label">Action type</label>
              <select v-model="filters.actionType" @change="loadLogs" class="hr-select" style="min-width: 180px;">
                <option value="">All</option>
                <option value="LOGIN">LOGIN</option>
                <option value="LOGOUT">LOGOUT</option>
                <option value="FAILED_LOGIN">FAILED_LOGIN</option>
                <option value="CREATE_USER">CREATE_USER</option>
                <option value="UPDATE_USER">UPDATE_USER</option>
                <option value="RESET_PASSWORD">RESET_PASSWORD</option>
                <option value="PASSWORD_CHANGE">PASSWORD_CHANGE</option>
                <option value="DELETE_USER">DELETE_USER</option>
                <option value="SUSPEND_ACCOUNT">SUSPEND_ACCOUNT</option>
                <option value="ACTIVATE_ACCOUNT">ACTIVATE_ACCOUNT</option>
                <option value="APPROVE_USER">APPROVE_USER</option>
                <option value="REJECT_USER">REJECT_USER</option>
                <option value="UPDATE">UPDATE</option>
                <option value="DOCUMENT_UPLOAD">DOCUMENT_UPLOAD</option>
                <option value="DOCUMENT_DOWNLOAD">DOCUMENT_DOWNLOAD</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Username / ID</label>
              <input v-model="filters.username" type="text" class="hr-input" placeholder="Filter by user" style="width: 160px;" @keyup.enter="loadLogs">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">From date</label>
              <input v-model="filters.fromDate" type="date" class="hr-input" @change="loadLogs">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">To date</label>
              <input v-model="filters.toDate" type="date" class="hr-input" @change="loadLogs">
            </div>
            <button type="button" class="hr-btn hr-btn-primary" @click="loadLogs">Apply</button>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">Logs ({{ total }} total)</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p class="hr-text-muted">Loading audit logs...</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Description</th>
                  <th>Table / Record</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in logs" :key="log.actionDate + (log.description || '') + (log.username || '')">
                  <td>{{ formatDate(log.actionDate) }}</td>
                  <td>{{ log.username || log.idNo || '—' }}</td>
                  <td><span class="hr-badge hr-badge-info">{{ log.actionType || '—' }}</span></td>
                  <td class="hr-text-muted hr-small" style="max-width: 320px;">{{ truncate(log.description, 80) }}</td>
                  <td>{{ log.tableName ? log.tableName + (log.recordID ? ' #' + log.recordID : '') : '—' }}</td>
                </tr>
                <tr v-if="logs.length === 0">
                  <td colspan="5" class="hr-text-center hr-py-4 hr-text-muted">No audit logs found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "AuditLogs",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      logs: [],
      total: 0,
      filters: {
        actionType: "",
        username: "",
        fromDate: "",
        toDate: ""
      }
    };
  },
  mounted() {
    this.loadLogs();
  },
  methods: {
    async loadLogs() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getAuditLogs");
      data.append("limit", "200");
      data.append("offset", "0");
      if (this.filters.actionType) data.append("actionType", this.filters.actionType);
      if (this.filters.username) data.append("username", this.filters.username.trim());
      if (this.filters.fromDate) data.append("fromDate", this.filters.fromDate);
      if (this.filters.toDate) data.append("toDate", this.filters.toDate);
      try {
        const res = await execute(data, authUrl);
        const d = res?.data?.data;
        this.logs = Array.isArray(d?.logs) ? d.logs : [];
        this.total = d?.total ?? 0;
      } catch (_) {
        this.logs = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleString();
    },
    truncate(str, len) {
      if (!str) return "—";
      return str.length <= len ? str : str.slice(0, len) + "…";
    }
  }
};
</script>

<style scoped>
.audit-logs-content { padding: 0; }
.hr-small { font-size: 0.875rem; }
</style>
